import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getAlumniLookups, getGraduationPreview, graduateStudent } from '../../api/alumni';
import { Alumni, AlumniStatus } from '../../types/alumni';
import { ALUMNI_STATUS_LABELS, DUPLICATE_REASON_LABELS, formatDay } from './alumniLabels';

const errorText = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? "Couldn't record the graduation") : "Couldn't record the graduation";

const today = () => new Date().toISOString().slice(0, 10);

/** Mark as Graduated (Phase 105): a review-and-confirm step before a
 * student becomes an alumnus. Shows the details the alumni record will
 * start from (editable here), lets the reviewer link a matching existing
 * alumni record instead of creating a new one, and records the graduation
 * date. Admission, departure and visa milestones never do this — only this
 * explicit action. The student's own records are not changed apart from
 * the graduation date. */
export const MarkGraduatedDialog = ({
  studentId,
  open,
  onClose,
  onDone,
}: {
  studentId: number;
  open: boolean;
  onClose: () => void;
  onDone: (alumni: Alumni) => void;
}) => {
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: ['graduation-preview', studentId],
    queryFn: () => getGraduationPreview(studentId),
    enabled: open,
    staleTime: 0,
  });
  const lookups = useQuery({ queryKey: ['alumni-lookups'], queryFn: getAlumniLookups, enabled: open });
  const [graduatedAt, setGraduatedAt] = useState(today());
  const [choice, setChoice] = useState<string>('new');
  const [details, setDetails] = useState({ email: '', phone: '', employer: '', jobTitle: '', countryOfWorkId: '', status: '' });

  useEffect(() => {
    if (preview.data) {
      const p = preview.data.prefill;
      setDetails({ email: p.email ?? '', phone: p.phone ?? '', employer: '', jobTitle: '', countryOfWorkId: '', status: '' });
      setChoice('new');
    }
  }, [preview.data]);

  const save = useMutation({
    mutationFn: () =>
      graduateStudent(studentId, {
        graduatedAt,
        ...(choice !== 'new'
          ? { linkAlumniId: Number(choice) }
          : {
              alumni: {
                email: details.email.trim() || null,
                phone: details.phone.trim() || undefined,
                employer: details.employer.trim() || null,
                jobTitle: details.jobTitle.trim() || null,
                countryOfWorkId: details.countryOfWorkId ? Number(details.countryOfWorkId) : null,
                status: (details.status || null) as AlumniStatus | null,
              },
            }),
      }),
    onSuccess: (alumni) => {
      queryClient.invalidateQueries({ queryKey: ['student', studentId] });
      queryClient.invalidateQueries({ queryKey: ['student-timeline', studentId] });
      queryClient.invalidateQueries({ queryKey: ['alumni'] });
      queryClient.invalidateQueries({ queryKey: ['alumni-stats'] });
      onDone(alumni);
      onClose();
    },
  });

  const data = preview.data;
  const linkable = data?.duplicates.filter((d) => d.linkable) ?? [];
  const dateInvalid = !graduatedAt || graduatedAt > today();

  return (
    <Dialog open={open} onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="mark-graduated-title">
      <DialogTitle id="mark-graduated-title">Mark as Graduated</DialogTitle>
      <DialogContent dividers>
        {preview.isLoading && <Skeleton variant="rounded" height={200} />}
        {preview.isError && <Alert severity="error">{errorText(preview.error)}</Alert>}
        {data?.alumni && (
          <Alert severity="info">
            {data.student.name} is already an alumnus —{' '}
            <Link component={RouterLink} to={`/alumni/${data.alumni.id}`}>
              {data.alumni.alumniNumber}
            </Link>
            {data.student.graduatedAt ? `, graduated ${formatDay(data.student.graduatedAt)}` : ''}.
          </Alert>
        )}
        {data && !data.alumni && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Confirm that <strong>{data.student.name}</strong> ({data.student.studentNumber}) has graduated. Their alumni
              record will start from the details below. Their payments, documents, applications and timeline stay as
              they are.
            </Typography>
            <TextField
              type="date"
              label="Graduation date"
              required
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today() }}
              value={graduatedAt}
              onChange={(e) => setGraduatedAt(e.target.value)}
              error={dateInvalid}
              helperText={dateInvalid ? 'Enter a date that is not in the future' : `Graduation year ${graduatedAt.slice(0, 4)}`}
              sx={{ maxWidth: 260 }}
            />

            {linkable.length > 0 && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1 }}>
                  An existing alumni record looks like the same person. Link it instead of creating a duplicate?
                </Alert>
                <RadioGroup value={choice} onChange={(e) => setChoice(e.target.value)}>
                  {linkable.map((d) => (
                    <FormControlLabel
                      key={d.id}
                      value={String(d.id)}
                      control={<Radio />}
                      label={`Link ${d.alumniNumber} — ${d.fullName} (${d.reasons.map((r) => DUPLICATE_REASON_LABELS[r] ?? r).join(', ')})`}
                    />
                  ))}
                  <FormControlLabel value="new" control={<Radio />} label="Create a new alumni record" />
                </RadioGroup>
              </Box>
            )}

            {choice === 'new' && (
              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>From the student record:</strong> {data.prefill.fullName}
                    {data.prefill.university ? ` · ${data.prefill.university}` : ''}
                    {data.prefill.course ? ` · ${data.prefill.course}` : ''}
                    {data.prefill.intake ? ` · ${data.prefill.intake}` : ''}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Contact number" value={details.phone} onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Email" value={details.email} onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Place of work (if known)" value={details.employer} onChange={(e) => setDetails((d) => ({ ...d, employer: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Job title" value={details.jobTitle} onChange={(e) => setDetails((d) => ({ ...d, jobTitle: e.target.value }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Country of work" value={details.countryOfWorkId} onChange={(e) => setDetails((d) => ({ ...d, countryOfWorkId: e.target.value }))} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 300 } } } }}>
                    <MenuItem value="">—</MenuItem>
                    {(lookups.data?.countries ?? []).map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Alumni status" value={details.status} onChange={(e) => setDetails((d) => ({ ...d, status: e.target.value }))}>
                    <MenuItem value="">—</MenuItem>
                    {(Object.keys(ALUMNI_STATUS_LABELS) as AlumniStatus[]).map((s) => (
                      <MenuItem key={s} value={s}>
                        {ALUMNI_STATUS_LABELS[s]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            )}
            {save.isError && <Alert severity="error">{errorText(save.error)}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={save.isPending}>
          {data?.alumni ? 'Close' : 'Cancel'}
        </Button>
        {data && !data.alumni && (
          <Button variant="contained" disabled={save.isPending || dateInvalid} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : choice === 'new' ? 'Confirm graduation' : 'Confirm and link'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
